import { SESSION_CONFIG } from './config';

/**
 * Session expiration types - supports both server (Date) and client (string) timestamps
 */
export type SessionTimestamps = {
  startedAt: Date | string;
  lastSeenAt?: Date | string | null;
};

/**
 * Check if a session has expired based on TTL.
 * A session is expired if either:
 * - Time since start exceeds TTL
 * - Time since last activity exceeds TTL
 *
 * @param session - Object with startedAt and lastSeenAt timestamps
 * @param now - Current time (Date object)
 * @param ttlMs - TTL in milliseconds (defaults to SESSION_CONFIG.SESSION_TTL_MS)
 * @returns true if session is expired
 */
export const isSessionExpired = (
  session: SessionTimestamps,
  now: Date,
  ttlMs: number = SESSION_CONFIG.SESSION_TTL_MS
): boolean => {
  const startedAt =
    session.startedAt instanceof Date
      ? session.startedAt
      : new Date(session.startedAt);

  // == null catches both null and undefined
  const lastSeenAt =
    session.lastSeenAt == null
      ? null
      : session.lastSeenAt instanceof Date
        ? session.lastSeenAt
        : new Date(session.lastSeenAt);

  const sinceStart = now.getTime() - startedAt.getTime();
  const sinceLastSeen =
    !lastSeenAt || Number.isNaN(lastSeenAt.getTime())
      ? Number.POSITIVE_INFINITY
      : now.getTime() - lastSeenAt.getTime();

  return sinceStart > ttlMs || sinceLastSeen > ttlMs;
};
